import { create } from 'zustand'
import { getFirestoreUtils } from '@/lib/firebase'
import { useAuthStore } from './authStore'
import {
  createSharedGroup, updateSharedGroup as updateGroupService,
  addSharedTask, updateSharedTask, toggleSharedTaskComplete,
  deleteSharedTask, assignSharedTask, sendInvitation,
  callAcceptInvitation, callDeclineInvitation,
  callLeaveGroup, callDeleteSharedGroup,
  callRemoveMember, callTransferOwnership, callChangeMemberRole,
} from '@/services/sharedGroupService'
import {
  uploadGroupFile, deleteGroupFile,
  addReaction as addReactionService, removeReaction as removeReactionService,
  addComment as addCommentService, deleteComment as deleteCommentService,
  addSubtask as addSubtaskService, toggleSubtask as toggleSubtaskService, deleteSubtask as deleteSubtaskService,
  createLabel as createLabelService, deleteLabel as deleteLabelService,
  addLabelToTask as addLabelToTaskService, removeLabelFromTask as removeLabelFromTaskService,
  uploadChatImage as uploadChatImageService,
  uploadChatVoice as uploadChatVoiceService,
  uploadChatFile as uploadChatFileService,
  pinTask as pinTaskService, unpinTask as unpinTaskService,
} from '@/services/sharedGroupService'
import {
  sendChatMessage, deleteChatMessage,
  addChatReaction as addChatReactionService,
  removeChatReaction as removeChatReactionService,
  sendSystemMessage as sendSystemMessageService,
  updateChatReadPosition as updateChatReadPositionService,
  updateTypingStatus as updateTypingStatusService,
  updatePresence as updatePresenceService,
  snapshotToChatMessage,
} from '@/services/chatRtdbService'
import type {
  SharedGroup, SharedTask, SharedGroupMember,
  GroupInvitation, GroupActivity, GroupFile,
  TaskComment, TaskSubtask, GroupLabel, ChatMessage,
} from '@/lib/sharedTypes'

// 구독 세대 ID — Date.now() 대신 단조 증가 카운터로 밀리초 충돌 방지
let _subIdCounter = 0
const MAX_SUB_RETRIES = 3
// 모듈 레벨 재시도 추적
const _groupRetryCount = new Map<string, number>()
// 건강 체크: 마지막 onSnapshot 수신 시각 + 인터벌
let _lastSnapshotAt = Date.now()
let _healthCheckInterval: ReturnType<typeof setInterval> | null = null

interface SharedGroupState {
  // 데이터
  sharedGroups: SharedGroup[]
  activeGroupId: string | null
  activeGroupTasks: SharedTask[]
  activeGroupMembers: SharedGroupMember[]
  pendingInvitations: GroupInvitation[]
  activityLog: GroupActivity[]
  isLoading: boolean
  syncError: string | null // H2: 리스너 에러 상태 추적

  // 새로운 데이터
  activeTaskComments: TaskComment[]
  activeTaskSubtasks: TaskSubtask[]
  groupLabels: GroupLabel[]
  chatMessages: ChatMessage[]
  _olderChatMessages: ChatMessage[]
  _pendingChatMessages: ChatMessage[]  // 낙관적 UI용 — 전송 중 메시지
  _pendingTaskToggles: Set<string>     // 낙관적 UI용 — 완료 토글 전송 중인 task ID
  groupFiles: GroupFile[]

  // 구독 해제 함수 보관
  _globalUnsubs: (() => void)[]
  _detailUnsubs: (() => void)[]
  _commentUnsubs: (() => void)[]
  _subtaskUnsubs: (() => void)[]
  _chatUnsubs: (() => void)[]

  // 레이스 컨디션 방지용 구독 세대 ID
  _detailSubId: number
  _commentSubId: number
  _subtaskSubId: number
  _groupSubId: number

  // 초기화
  initialize: () => Promise<void>
  cleanup: () => void

  // 그룹 CRUD
  createGroup: (name: string, color: string, description?: string) => Promise<string>
  updateGroup: (groupId: string, updates: Partial<SharedGroup>) => Promise<void>
  deleteGroup: (groupId: string) => Promise<void>

  // 할 일
  addTask: (groupId: string, task: Omit<Partial<SharedTask>, 'attachments'> & {
    subtasks?: { title: string }[]
    attachments?: File[]
  }) => Promise<void>
  updateTask: (groupId: string, taskId: string, updates: Partial<SharedTask>) => Promise<void>
  toggleTask: (groupId: string, taskId: string, currentStatus: 'pending' | 'completed') => Promise<void>
  deleteTask: (groupId: string, taskId: string) => Promise<void>
  assignTask: (groupId: string, taskId: string, assigneeIds: string[] | null, assigneeNames: string[] | null) => Promise<void>

  // 초대
  invite: (groupId: string, groupName: string, groupColor: string, email: string, role: 'editor' | 'viewer') => Promise<void>
  acceptInvitation: (invitationId: string) => Promise<void>
  declineInvitation: (invitationId: string) => Promise<void>

  // 멤버
  leaveGroup: (groupId: string) => Promise<void>
  removeMember: (groupId: string, targetUid: string) => Promise<void>
  transferOwnership: (groupId: string, newOwnerId: string) => Promise<void>
  changeMemberRole: (groupId: string, targetUid: string, newRole: 'editor' | 'editor-limited' | 'viewer') => Promise<void>

  // 그룹 상세 구독
  subscribeToGroup: (groupId: string, force?: boolean) => Promise<void>
  unsubscribeFromGroup: () => void
  loadActivityLog: (groupId: string) => Promise<void>
  loadLabels: (groupId: string) => Promise<void>

  // 리액션
  addReaction: (groupId: string, taskId: string, emoji: string) => Promise<void>
  removeReaction: (groupId: string, taskId: string, emoji: string) => Promise<void>

  // 댓글
  addComment: (groupId: string, taskId: string, text: string, mentions?: string[], parentId?: string, image?: { url: string; width: number; height: number }) => Promise<void>
  deleteComment: (groupId: string, taskId: string, commentId: string) => Promise<void>
  subscribeToComments: (groupId: string, taskId: string) => Promise<void>
  unsubscribeFromComments: () => void

  // 서브태스크
  addSubtask: (groupId: string, taskId: string, title: string) => Promise<void>
  toggleSubtask: (groupId: string, taskId: string, subtaskId: string, currentCompleted: boolean) => Promise<void>
  deleteSubtask: (groupId: string, taskId: string, subtaskId: string, wasCompleted: boolean) => Promise<void>
  subscribeToSubtasks: (groupId: string, taskId: string) => Promise<void>
  unsubscribeFromSubtasks: () => void

  // 라벨
  createLabel: (groupId: string, name: string, color: string) => Promise<string>
  deleteLabel: (groupId: string, labelId: string) => Promise<void>
  addLabelToTask: (groupId: string, taskId: string, labelId: string) => Promise<void>
  removeLabelFromTask: (groupId: string, taskId: string, labelId: string) => Promise<void>

  // 채팅
  hasMoreMessages: boolean
  loadMoreMessages: (groupId: string) => Promise<void>
  updateTypingStatus: (groupId: string, isTyping: boolean) => Promise<void>
  sendChat: (groupId: string, payload: import('@/lib/sharedTypes').ChatMessagePayload) => Promise<void>
  deleteChat: (groupId: string, messageId: string) => Promise<void>
  uploadChatImage: (groupId: string, file: File) => Promise<{ url: string; width: number; height: number; name: string }>
  uploadChatVoice: (groupId: string, blob: Blob) => Promise<{ url: string; duration: number }>
  uploadChatFile: (groupId: string, file: File) => Promise<{ url: string; name: string; size: number; type: string }>
  addChatReaction: (groupId: string, messageId: string, emoji: string) => Promise<void>
  removeChatReaction: (groupId: string, messageId: string, emoji: string) => Promise<void>
  updateChatReadPosition: (groupId: string) => Promise<void>
  toggleMute: (groupId: string) => Promise<void>
  sendSystemMessage: (groupId: string, action: string, actorName?: string, targetTitle?: string) => Promise<void>

  // 핀
  pinTask: (groupId: string, taskId: string) => Promise<void>
  unpinTask: (groupId: string, taskId: string) => Promise<void>

  // 파일
  uploadFile: (groupId: string, file: File) => Promise<void>
  deleteFile: (groupId: string, fileId: string, fileUrl: string, fileName: string) => Promise<void>

  // 온라인
  updatePresence: (groupId: string, isOnline: boolean) => Promise<void>
}

export const useSharedGroupStore = create<SharedGroupState>()((set, get) => ({
  sharedGroups: [],
  activeGroupId: null,
  activeGroupTasks: [],
  activeGroupMembers: [],
  pendingInvitations: [],
  activityLog: [],
  isLoading: false,
  syncError: null,
  activeTaskComments: [],
  activeTaskSubtasks: [],
  groupLabels: [],
  chatMessages: [],
  _olderChatMessages: [],
  _pendingChatMessages: [],
  _pendingTaskToggles: new Set<string>(),
  hasMoreMessages: true,
  groupFiles: [],
  _globalUnsubs: [],
  _detailUnsubs: [],
  _commentUnsubs: [],
  _subtaskUnsubs: [],
  _chatUnsubs: [],
  _detailSubId: 0,
  _commentSubId: 0,
  _subtaskSubId: 0,
  _groupSubId: 0,

  // ─── 초기화: 내 공유 그룹 + 초대 실시간 구독 ───
  initialize: async () => {
    // 이전 구독 정리 (중복 초기화 방어 — onAuthStateChanged 재호출 시)
    get()._globalUnsubs.forEach((unsub) => unsub())
    set({ _globalUnsubs: [] })

    const user = useAuthStore.getState().user
    if (!user) return

    set({ isLoading: true })

    try {
      const { firestore, collection, onSnapshot } = await getFirestoreUtils()
      const { query, where } = await import('firebase/firestore')
      const unsubs: (() => void)[] = []

      // 1) 내가 멤버인 공유 그룹 구독
      const q = query(
        collection(firestore, 'sharedGroups'),
        where('memberIds', 'array-contains', user.uid)
      )

      const unsubGroups = onSnapshot(q, (snapshot) => {
        const groups: SharedGroup[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as SharedGroup[]
        set({ sharedGroups: groups, isLoading: false, syncError: null })
      }, (err) => {
        // H2: 에러 상태 저장 + 토스트 알림
        const msg = err instanceof Error ? err.message : '동기화 오류'
        set({ isLoading: false, syncError: msg })
        import('@/stores/toastStore').then(({ useToastStore }) => {
          useToastStore.getState().showToast('공유 그룹 동기화에 실패했습니다. 새로고침을 시도하세요.', 'error')
        }).catch(() => {})
      })
      unsubs.push(unsubGroups)

      // 2) 나에게 온 초대 구독
      const invQuery = query(
        collection(firestore, 'invitations'),
        where('inviteeEmail', '==', user.email),
        where('status', '==', 'pending')
      )

      const unsubInv = onSnapshot(invQuery, (snapshot) => {
        const invitations: GroupInvitation[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as GroupInvitation[]
        set({ pendingInvitations: invitations })
      })
      unsubs.push(unsubInv)

      set({ _globalUnsubs: unsubs })
    } catch (err) {
      console.error('SharedGroupStore initialize failed:', err)
      set({ isLoading: false })
    }
  },

  cleanup: () => {
    get()._globalUnsubs.forEach((unsub) => unsub())
    get()._detailUnsubs.forEach((unsub) => unsub())
    get()._commentUnsubs.forEach((unsub) => unsub())
    get()._subtaskUnsubs.forEach((unsub) => unsub())
    get()._chatUnsubs.forEach((unsub) => unsub())
    ++_subIdCounter // 진행 중인 모든 구독 무효화
    if (_healthCheckInterval) { clearInterval(_healthCheckInterval); _healthCheckInterval = null }
    set({
      sharedGroups: [],
      activeGroupId: null,
      activeGroupTasks: [],
      activeGroupMembers: [],
      pendingInvitations: [],
      activityLog: [],
      activeTaskComments: [],
      activeTaskSubtasks: [],
      groupLabels: [],
      chatMessages: [],
      _olderChatMessages: [],
      _pendingChatMessages: [],
      _pendingTaskToggles: new Set<string>(),
      groupFiles: [],
      _globalUnsubs: [],
      _detailUnsubs: [],
      _commentUnsubs: [],
      _subtaskUnsubs: [],
      _chatUnsubs: [],
    })
  },

  // ─── 그룹 CRUD ───
  createGroup: async (name, color, description) => {
    return await createSharedGroup(name, color, description)
  },

  updateGroup: async (groupId, updates) => {
    await updateGroupService(groupId, updates)
  },

  deleteGroup: async (groupId) => {
    await callDeleteSharedGroup(groupId)
    if (get().activeGroupId === groupId) {
      set({ activeGroupId: null, activeGroupTasks: [], activeGroupMembers: [] })
    }
  },

  // ─── 할 일 ───
  addTask: async (groupId, task) => {
    await addSharedTask(groupId, task as Parameters<typeof addSharedTask>[1])
  },

  updateTask: async (groupId, taskId, updates) => {
    await updateSharedTask(groupId, taskId, updates)
  },

  toggleTask: async (groupId, taskId, currentStatus) => {
    const newStatus: 'pending' | 'completed' = currentStatus === 'pending' ? 'completed' : 'pending'

    // 낙관적 UI: Firestore 쓰기 전에 activeGroupTasks를 즉시 패치.
    // onSnapshot 에코가 서버 진실로 덮어쓰므로 같은 shape(status만 변경)으로 깜빡임 방지.
    // 롤백을 위해 이전 스냅샷 보존.
    const prevTasks = get().activeGroupTasks
    set((state) => {
      const nextPending = new Set(state._pendingTaskToggles)
      nextPending.add(taskId)
      return {
        activeGroupTasks: state.activeGroupTasks.map((t) =>
          t.id === taskId ? { ...t, status: newStatus } : t
        ),
        _pendingTaskToggles: nextPending,
      }
    })

    try {
      await toggleSharedTaskComplete(groupId, taskId, currentStatus)
    } catch (err) {
      // 실패: 이전 스냅샷으로 롤백 (단, 그 사이 onSnapshot이 갱신했을 수 있으므로
      //       해당 task만 이전 값으로 되돌린다)
      const prevTask = prevTasks.find((t) => t.id === taskId)
      set((state) => ({
        activeGroupTasks: prevTask
          ? state.activeGroupTasks.map((t) => (t.id === taskId ? prevTask : t))
          : state.activeGroupTasks,
      }))
      import('@/stores/toastStore').then(({ useToastStore }) => {
        useToastStore.getState().showToast('작업 상태 변경에 실패했습니다.', 'error')
      }).catch(() => {})
      // 롤백+토스트로 완전히 처리됨 — 호출자가 모두 fire-and-forget이므로
      // 재던지면 unhandled rejection만 발생. 로그만 남긴다.
      console.error('toggleTask 실패:', err)
    } finally {
      // pending 해제 — onSnapshot 에코가 도착하면 서버 진실로 정렬됨
      set((state) => {
        const nextPending = new Set(state._pendingTaskToggles)
        nextPending.delete(taskId)
        return { _pendingTaskToggles: nextPending }
      })
    }
  },

  deleteTask: async (groupId, taskId) => {
    await deleteSharedTask(groupId, taskId)
  },

  assignTask: async (groupId, taskId, assigneeIds, assigneeNames) => {
    await assignSharedTask(groupId, taskId, assigneeIds, assigneeNames)
  },

  // ─── 초대 ───
  invite: async (groupId, groupName, groupColor, email, role) => {
    await sendInvitation(groupId, groupName, groupColor, email, role)
  },

  acceptInvitation: async (invitationId) => {
    await callAcceptInvitation(invitationId)
  },

  declineInvitation: async (invitationId) => {
    await callDeclineInvitation(invitationId)
  },

  // ─── 멤버 ───
  leaveGroup: async (groupId) => {
    await callLeaveGroup(groupId)
  },

  removeMember: async (groupId, targetUid) => {
    await callRemoveMember(groupId, targetUid)
  },

  transferOwnership: async (groupId, newOwnerId) => {
    await callTransferOwnership(groupId, newOwnerId)
  },

  changeMemberRole: async (groupId, targetUid, newRole) => {
    await callChangeMemberRole(groupId, targetUid, newRole)
  },

  // ─── 그룹 상세 실시간 구독 ───
  subscribeToGroup: async (groupId: string, force = false) => {
    const prevGroupId = get().activeGroupId
    const isGroupSwitch = prevGroupId !== groupId

    // DEDUP: 같은 그룹 + 활성 리스너 → 재구독 불필요
    // 단, force(건강 체크/재연결 버튼) 또는 syncError 상태에서는 우회하여 강제 재구독
    if (!force && !get().syncError && !isGroupSwitch && (get()._detailUnsubs.length > 0 || get()._chatUnsubs.length > 0)) {
      return
    }

    // 기존 구독 해제
    get()._detailUnsubs.forEach((unsub) => unsub())
    get()._chatUnsubs.forEach((unsub) => unsub())

    // 구독 세대 ID
    const subId = ++_subIdCounter
    _groupRetryCount.delete(groupId)

    // 그룹 전환 시에만 데이터 초기화 — 재구독 시 기존 데이터 유지
    if (isGroupSwitch) {
      set({
        activeGroupId: groupId,
        activeGroupTasks: [],
        activeGroupMembers: [],
        activityLog: [],
        groupLabels: [],
        chatMessages: [],
        _olderChatMessages: [],
        _pendingChatMessages: [],
        _pendingTaskToggles: new Set<string>(),
        hasMoreMessages: true,
        groupFiles: [],
        _detailUnsubs: [],
        _chatUnsubs: [],
        _groupSubId: subId,
      })
    } else {
      set({ _detailUnsubs: [], _chatUnsubs: [], _groupSubId: subId })
    }

    const guard = () => get()._groupSubId === subId
    const unsubs: (() => void)[] = []
    const chatUnsubs: (() => void)[] = []

    // ── 개별 리스너 재연결 (리스너별 독립 retry, 다른 리스너 무관) ──
    const _retryCounters = new Map<string, number>()
    const makeErrorHandler = (
      name: string,
      setup: () => (() => void),
      target: '_detailUnsubs' | '_chatUnsubs',
    ) => {
      return (err: unknown) => {
        console.error(`[sub] ${name} error:`, err)
        if (!guard()) return
        const retries = (_retryCounters.get(name) || 0) + 1
        _retryCounters.set(name, retries)
        if (retries > MAX_SUB_RETRIES) {
          console.warn(`[sub] ${name}: 최대 재시도(${MAX_SUB_RETRIES}회) 초과`)
          set({ syncError: `${name} 실시간 연결 끊김` })
          import('@/stores/toastStore').then(({ useToastStore }) => {
            useToastStore.getState().showToast('실시간 연결이 끊어졌습니다. 페이지를 새로고침하세요.', 'error')
          }).catch(() => {})
          return
        }
        const delay = Math.min(2000 * 2 ** (retries - 1), 10000)
        console.info(`[sub] ${name}: ${delay}ms 후 재연결 (${retries}/${MAX_SUB_RETRIES})`)
        setTimeout(() => {
          if (!guard()) return
          try {
            const newUnsub = setup()
            set((state) => ({ [target]: [...state[target], newUnsub] }))
          } catch (e) {
            console.error(`[sub] ${name}: 재연결 실패`, e)
          }
        }, delay)
      }
    }

    // ── RTDB 채팅/타이핑 구독 (Firestore와 독립 — 병렬 시작) ──
    const rtdbPromise = (async () => {
      try {
        const { getRtdbInstance } = await import('@/lib/firebase')
        const rtdb = await getRtdbInstance()
        const { ref, query: rtdbQuery, orderByChild, limitToLast, onValue, off } = await import('firebase/database')

        if (!guard()) return

        // 채팅 리스너 설정 (에러 시 재연결 가능하도록 함수로 분리)
        const setupChat = (): (() => void) => {
          const chatRef = rtdbQuery(
            ref(rtdb, `chatGroups/${groupId}/messages`),
            orderByChild('createdAt'),
            limitToLast(100)
          )
          onValue(chatRef, (snapshot) => {
            if (!guard()) return
            _lastSnapshotAt = Date.now()
            _retryCounters.delete('chat')
            const messages: ChatMessage[] = []
            snapshot.forEach((child) => {
              messages.push(snapshotToChatMessage(child.key!, child.val()))
            })
            // RTDB onValue는 오름차순 → 내림차순 변환 (chatMessages는 desc)
            messages.reverse()
            const confirmedIds = new Set(messages.map(m => m.id))
            set((state) => ({
              chatMessages: messages,
              _pendingChatMessages: state._pendingChatMessages.filter(m => !confirmedIds.has(m.id)),
              syncError: null,
            }))
          }, makeErrorHandler('chat', setupChat, '_chatUnsubs'))
          return () => off(chatRef)
        }
        chatUnsubs.push(setupChat())

        // 타이핑 리스너 설정
        const setupTyping = (): (() => void) => {
          const typingRef = ref(rtdb, `chatGroups/${groupId}/typing`)
          onValue(typingRef, (snapshot) => {
            if (!guard()) return
            _lastSnapshotAt = Date.now()
            const typingUids: string[] = []
            snapshot.forEach((child) => { if (child.val()) typingUids.push(child.key!) })
            set((state) => ({
              activeGroupMembers: state.activeGroupMembers.map((m) => ({
                ...m,
                isTyping: typingUids.includes(m.uid),
              })),
            }))
          }, makeErrorHandler('typing', setupTyping, '_chatUnsubs'))
          return () => off(typingRef)
        }
        chatUnsubs.push(setupTyping())
      } catch (err) {
        console.error('[subscribeToGroup] RTDB 구독 실패:', err)
      }
    })()

    // ── Firestore 구독 (할일, 멤버, 파일) ──
    const firestorePromise = (async () => {
      try {
        const { firestore, collection, onSnapshot } = await getFirestoreUtils()
        const { query, orderBy } = await import('firebase/firestore')

        if (!guard()) return

        // 1) 할 일 구독
        const setupTasks = () => onSnapshot(
          query(collection(firestore, `sharedGroups/${groupId}/tasks`), orderBy('sortOrder')),
          (snap) => {
            if (!guard()) return
            _retryCounters.delete('tasks'); _lastSnapshotAt = Date.now()
            set({ activeGroupTasks: snap.docs.map((d) => ({ id: d.id, ...d.data() })) as SharedTask[], syncError: null })
          },
          makeErrorHandler('tasks', setupTasks, '_detailUnsubs')
        )
        unsubs.push(setupTasks())

        // 2) 멤버 구독
        const setupMembers = () => onSnapshot(
          collection(firestore, `sharedGroups/${groupId}/members`),
          (snap) => {
            if (!guard()) return
            _retryCounters.delete('members'); _lastSnapshotAt = Date.now()
            // RTDB 유래 일시 플래그(isTyping)는 멤버 doc 갱신 시 사라지므로 이전 상태에서 보존
            set((state) => {
              const prevByUid = new Map(state.activeGroupMembers.map((m) => [m.uid, m]))
              return {
                activeGroupMembers: snap.docs
                  .map((d) => {
                    const data = d.data() as SharedGroupMember
                    const prev = prevByUid.get(data.uid)
                    return prev ? { ...data, isTyping: prev.isTyping } : data
                  })
                  .filter((m) => m.status === 'active'),
                syncError: null,
              }
            })
          },
          makeErrorHandler('members', setupMembers, '_detailUnsubs')
        )
        unsubs.push(setupMembers())

        // 3) 파일 구독
        const setupFiles = () => onSnapshot(
          query(collection(firestore, `sharedGroups/${groupId}/files`), orderBy('createdAt', 'desc')),
          (snap) => {
            if (!guard()) return
            _retryCounters.delete('files'); _lastSnapshotAt = Date.now()
            set({ groupFiles: snap.docs.map((d) => ({ id: d.id, ...d.data() })) as GroupFile[], syncError: null })
          },
          makeErrorHandler('files', setupFiles, '_detailUnsubs')
        )
        unsubs.push(setupFiles())
      } catch (err) {
        console.error('[subscribeToGroup] Firestore 구독 실패:', err)
        set({ syncError: '실시간 동기화 연결에 실패했습니다.' })
      }
    })()

    try {
      // RTDB + Firestore 병렬 대기
      await Promise.all([rtdbPromise, firestorePromise])

      if (!guard()) {
        unsubs.forEach((u) => u())
        chatUnsubs.forEach((u) => u())
        return
      }

      set({ _detailUnsubs: unsubs, _chatUnsubs: chatUnsubs, syncError: null })

      // Fix 5: 건강 체크 — 90초 이상 onSnapshot 없으면 재구독
      if (_healthCheckInterval) clearInterval(_healthCheckInterval)
      _lastSnapshotAt = Date.now()
      _healthCheckInterval = setInterval(() => {
        if (!guard() || document.visibilityState !== 'visible') {
          if (!guard() && _healthCheckInterval) { clearInterval(_healthCheckInterval); _healthCheckInterval = null }
          return
        }
        if (Date.now() - _lastSnapshotAt > 90_000) {
          console.warn('[healthCheck] 90초간 onSnapshot 없음, 재구독')
          _retryCounters.clear()
          get().subscribeToGroup(groupId, true)
        }
      }, 30_000)

      // Fix 4: 탭 복귀/네트워크 복구 시 구독 생존 확인
      import('@/lib/firebase').then(({ onConnectionRecovery }) => {
        const unsubRecovery = onConnectionRecovery(() => {
          if (!guard()) return
          _lastSnapshotAt = Date.now() // 건강 체크 타이머 리셋
        })
        // detached된 로컬 unsubs 배열 대신 현재 state 배열에 푸시 — 그룹 전환 레이스 시 누수 방지
        if (guard()) {
          set((s) => ({ _detailUnsubs: [...s._detailUnsubs, unsubRecovery] }))
        } else {
          unsubRecovery() // 세대 이미 만료 → 즉시 해제
        }
      }).catch(() => {})
    } catch (err) {
      console.error('[subscribeToGroup] 구독 설정 실패:', err)
      set({ syncError: '실시간 동기화 연결에 실패했습니다.' })
    }
  },

  unsubscribeFromGroup: () => {
    const gid = get().activeGroupId
    if (gid) _groupRetryCount.delete(gid)
    if (_healthCheckInterval) { clearInterval(_healthCheckInterval); _healthCheckInterval = null }
    get()._detailUnsubs.forEach((unsub) => unsub())
    get()._chatUnsubs.forEach((unsub) => unsub())
    set({
      activeGroupId: null,
      activeGroupTasks: [],
      activeGroupMembers: [],
      activityLog: [],
      groupLabels: [],
      chatMessages: [],
      _olderChatMessages: [],
      _pendingChatMessages: [],
      _pendingTaskToggles: new Set<string>(),
      groupFiles: [],
      _detailUnsubs: [],
      _chatUnsubs: [],
      // _groupSubId 리셋 안 함 — 진행 중인 subscribeToGroup의 guard() 보호
    })
  },

  // ─── On-demand 데이터 로딩 (리스너 절약) ───
  loadActivityLog: async (groupId: string) => {
    try {
      const { firestore, collection } = await getFirestoreUtils()
      const { query, orderBy, limit: limitFn, getDocs } = await import('firebase/firestore')
      const snap = await getDocs(query(
        collection(firestore, `sharedGroups/${groupId}/activity`),
        orderBy('createdAt', 'desc'), limitFn(200)
      ))
      set({ activityLog: snap.docs.map((d) => ({ id: d.id, ...d.data() })) as GroupActivity[] })
    } catch (err) {
      console.error('[loadActivityLog]', err)
    }
  },

  loadLabels: async (groupId: string) => {
    try {
      const { firestore, collection } = await getFirestoreUtils()
      const { getDocs } = await import('firebase/firestore')
      const snap = await getDocs(collection(firestore, `sharedGroups/${groupId}/labels`))
      set({ groupLabels: snap.docs.map((d) => ({ id: d.id, ...d.data() })) as GroupLabel[] })
    } catch (err) {
      console.error('[loadLabels]', err)
    }
  },

  // ─── 리액션 ───
  addReaction: async (groupId, taskId, emoji) => {
    await addReactionService(groupId, taskId, emoji)
  },

  removeReaction: async (groupId, taskId, emoji) => {
    await removeReactionService(groupId, taskId, emoji)
  },

  // ─── 댓글 ───
  addComment: async (groupId, taskId, text, mentions, parentId, image) => {
    await addCommentService(groupId, taskId, text, mentions, parentId, image)
  },

  deleteComment: async (groupId, taskId, commentId) => {
    await deleteCommentService(groupId, taskId, commentId)
  },

  subscribeToComments: async (groupId, taskId) => {
    // 기존 댓글 구독 해제
    get()._commentUnsubs.forEach((unsub) => unsub())

    const subId = ++_subIdCounter
    set({ activeTaskComments: [], _commentSubId: subId })

    const { firestore, collection, onSnapshot } = await getFirestoreUtils()
    const { query, orderBy } = await import('firebase/firestore')

    // 레이스 컨디션 방지
    if (get()._commentSubId !== subId) return

    const unsubs: (() => void)[] = []

    const commentsQuery = query(
      collection(firestore, `sharedGroups/${groupId}/tasks/${taskId}/comments`),
      orderBy('createdAt', 'asc')
    )

    const unsubComments = onSnapshot(commentsQuery, (snapshot) => {
      const comments: TaskComment[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as TaskComment[]
      set({ activeTaskComments: comments })
    }, (error) => {
      console.error(`[subscribeToComments] error (task ${taskId}):`, error)
    })
    unsubs.push(unsubComments)

    set({ _commentUnsubs: unsubs })
  },

  unsubscribeFromComments: () => {
    get()._commentUnsubs.forEach((unsub) => unsub())
    set({ activeTaskComments: [], _commentUnsubs: [] })
  },

  // ─── 서브태스크 ───
  addSubtask: async (groupId, taskId, title) => {
    await addSubtaskService(groupId, taskId, title)
  },

  toggleSubtask: async (groupId, taskId, subtaskId, currentCompleted) => {
    await toggleSubtaskService(groupId, taskId, subtaskId, currentCompleted)
  },

  deleteSubtask: async (groupId, taskId, subtaskId, wasCompleted) => {
    await deleteSubtaskService(groupId, taskId, subtaskId, wasCompleted)
  },

  subscribeToSubtasks: async (groupId, taskId) => {
    // 기존 서브태스크 구독 해제
    get()._subtaskUnsubs.forEach((unsub) => unsub())

    const subId = ++_subIdCounter
    set({ activeTaskSubtasks: [], _subtaskSubId: subId })

    const { firestore, collection, onSnapshot } = await getFirestoreUtils()
    const { query, orderBy } = await import('firebase/firestore')

    // 레이스 컨디션 방지
    if (get()._subtaskSubId !== subId) return

    const unsubs: (() => void)[] = []

    const subtasksQuery = query(
      collection(firestore, `sharedGroups/${groupId}/tasks/${taskId}/subtasks`),
      orderBy('sortOrder')
    )

    const unsubSubtasks = onSnapshot(subtasksQuery, (snapshot) => {
      const subtasks: TaskSubtask[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as TaskSubtask[]
      set({ activeTaskSubtasks: subtasks })
    }, (error) => {
      console.error(`[subscribeToSubtasks] error (task ${taskId}):`, error)
    })
    unsubs.push(unsubSubtasks)

    set({ _subtaskUnsubs: unsubs })
  },

  unsubscribeFromSubtasks: () => {
    get()._subtaskUnsubs.forEach((unsub) => unsub())
    set({ activeTaskSubtasks: [], _subtaskUnsubs: [] })
  },

  // ─── 라벨 ───
  createLabel: async (groupId, name, color) => {
    return await createLabelService(groupId, name, color)
  },

  deleteLabel: async (groupId, labelId) => {
    await deleteLabelService(groupId, labelId)
  },

  addLabelToTask: async (groupId, taskId, labelId) => {
    await addLabelToTaskService(groupId, taskId, labelId)
  },

  removeLabelFromTask: async (groupId, taskId, labelId) => {
    await removeLabelFromTaskService(groupId, taskId, labelId)
  },

  // ─── 채팅 ───
  loadMoreMessages: async (groupId) => {
    const chatMsgs = get().chatMessages
    const olderMsgs = get()._olderChatMessages
    const allMsgs = [...chatMsgs, ...olderMsgs]
    if (allMsgs.length === 0) return
    const oldest = [...allMsgs].sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0]
    try {
      const { getRtdbInstance } = await import('@/lib/firebase')
      const rtdb = await getRtdbInstance()
      const { ref, query: rtdbQuery, orderByChild, endBefore, limitToLast, get: rtdbGet } = await import('firebase/database')
      const olderQuery = rtdbQuery(
        ref(rtdb, `chatGroups/${groupId}/messages`),
        orderByChild('createdAt'),
        endBefore(oldest.createdAt),
        limitToLast(50)
      )
      const snapshot = await rtdbGet(olderQuery)
      if (!snapshot.exists()) {
        set({ hasMoreMessages: false })
        return
      }
      const fetched: import('@/lib/sharedTypes').ChatMessage[] = []
      snapshot.forEach((child) => {
        fetched.push(snapshotToChatMessage(child.key!, child.val()))
      })
      set((state) => {
        // 그룹 전환 중이면 무시
        if (state.activeGroupId !== groupId) return state
        const existingIds = new Set([
          ...state.chatMessages.map(m => m.id),
          ...state._olderChatMessages.map(m => m.id),
        ])
        const newMessages = fetched.filter(m => !existingIds.has(m.id))
        return {
          _olderChatMessages: [...state._olderChatMessages, ...newMessages],
          hasMoreMessages: fetched.length >= 50,
        }
      })
    } catch (err) {
      console.error('loadMoreMessages failed:', err)
    }
  },

  updateTypingStatus: async (groupId, isTyping) => {
    try {
      await updateTypingStatusService(groupId, isTyping)
    } catch { /* 타이핑 상태 업데이트 실패 무시 */ }
  },

  sendChat: async (groupId, payload) => {
    const user = useAuthStore.getState().user
    if (!user) throw new Error('로그인 필요')

    // 클라이언트에서 doc ID 미리 생성 — optimistic 메시지와 Firestore 문서가 동일 ID
    const docId = crypto.randomUUID().replace(/-/g, '').slice(0, 20)
    const now = new Date().toISOString()

    const optimistic: ChatMessage = {
      id: docId,
      userId: user.uid,
      userName: user.displayName || '',
      userPhotoURL: user.photoURL || '',
      text: payload.text,
      type: payload.type || 'text',
      mentions: payload.mentions || [],
      imageUrl: payload.imageUrl,
      imageWidth: payload.imageWidth,
      imageHeight: payload.imageHeight,
      imageName: payload.imageName,
      taskSnapshot: payload.taskSnapshot,
      memoSnapshot: payload.memoSnapshot,
      replyTo: payload.replyTo,
      systemEvent: payload.systemEvent,
      voiceUrl: payload.voiceUrl,
      voiceDuration: payload.voiceDuration,
      fileUrl: payload.fileUrl,
      fileName: payload.fileName,
      fileSize: payload.fileSize,
      fileType: payload.fileType,
      reactions: {},
      createdAt: now,
    }

    // 1) 즉시 UI에 표시
    set((state) => ({
      _pendingChatMessages: [...state._pendingChatMessages, optimistic],
    }))

    try {
      // 2) 같은 docId로 Firebase에 저장 → onSnapshot이 같은 ID로 도착 → pending 자동 제거
      await sendChatMessage(groupId, payload, docId)
    } catch (err) {
      // 3) 실패: pending에서 제거
      set((state) => ({
        _pendingChatMessages: state._pendingChatMessages.filter(m => m.id !== docId),
      }))
      throw err
    }
  },

  deleteChat: async (groupId, messageId) => {
    await deleteChatMessage(groupId, messageId)
  },

  uploadChatImage: async (groupId, file) => {
    return await uploadChatImageService(groupId, file)
  },
  uploadChatVoice: async (groupId, blob) => {
    return await uploadChatVoiceService(groupId, blob)
  },
  uploadChatFile: async (groupId, file) => {
    return await uploadChatFileService(groupId, file)
  },
  addChatReaction: async (groupId, messageId, emoji) => {
    await addChatReactionService(groupId, messageId, emoji)
  },
  removeChatReaction: async (groupId, messageId, emoji) => {
    await removeChatReactionService(groupId, messageId, emoji)
  },
  updateChatReadPosition: async (groupId) => {
    await updateChatReadPositionService(groupId)
  },
  toggleMute: async (groupId) => {
    try {
      const user = useAuthStore.getState().user
      if (!user) return
      const member = get().activeGroupMembers.find((m) => m.uid === user.uid)
      if (!member) return
      const newMuted = !member.isMuted
      const { firestore, doc, setDoc } = await getFirestoreUtils()
      await setDoc(doc(firestore, `sharedGroups/${groupId}/members/${user.uid}`), {
        isMuted: newMuted,
      }, { merge: true })
    } catch { /* 뮤트 토글 실패 무시 */ }
  },
  sendSystemMessage: async (groupId, action, actorName, targetTitle) => {
    await sendSystemMessageService(groupId, action, actorName, targetTitle)
  },

  // ─── 핀 ───
  pinTask: async (groupId, taskId) => {
    await pinTaskService(groupId, taskId)
  },

  unpinTask: async (groupId, taskId) => {
    await unpinTaskService(groupId, taskId)
  },

  // ─── 온라인 ───
  // ─── 파일 ───
  uploadFile: async (groupId, file) => {
    await uploadGroupFile(groupId, file)
  },

  deleteFile: async (groupId, fileId, fileUrl, fileName) => {
    await deleteGroupFile(groupId, fileId, fileUrl, fileName)
  },

  updatePresence: async (groupId, isOnline) => {
    await updatePresenceService(groupId, isOnline)
  },
}))
